"use client";

import { cn } from "@layer/utils/cn";
import type * as React from "react";

function Table({ className, ...props }: React.ComponentProps<"table">) {
	return (
		<div data-slot="table-container" className="relative w-full">
			<table
				data-slot="table"
				className={cn("w-full caption-bottom text-sm", className)}
				{...props}
			/>
		</div>
	);
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
	return (
		<thead
			data-slot="table-header"
			className={cn("", className)}
			{...props}
		/>
	);
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
	return (
		<tbody
			data-slot="table-body"
			className={cn("[&_tr:last-child]:border-0 bg-app-bg/40", className)}
			{...props}
		/>
	);
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
	return (
		<tfoot
			data-slot="table-footer"
			className={cn(
				"border-t border-app-border-strong/80 font-medium [&>tr]:last:border-b-0",
				className,
			)}
			{...props}
		/>
	);
}

interface TableRowProps extends React.ComponentProps<"tr"> {
	header?: boolean;
	empty?: boolean;
}

function TableRow({ className, header, empty, ...props }: TableRowProps) {
	return (
		<tr
			data-slot="table-row"
			className={cn(
				"hover:bg-app-bg-surface-subtle data-[state=selected]:bg-app-bg-surface-strong transition-colors",
				header && "h-full",
				!header && "border-b border-app-border-strong/60",
				empty && "border-none",
				className,
			)}
			{...props}
		/>
	);
}

interface TableHeadProps extends React.ComponentProps<"th"> {
	isRunbook?: boolean;
}

function TableHead({ className, isRunbook, style, ...props }: TableHeadProps) {
	return (
		<th
			data-slot="table-head"
			className={cn(
				"sticky top-0 z-10 h-10 bg-app-bg-surface-strong/95 px-2 text-left align-middle font-medium whitespace-nowrap text-app-text",
				"border-0! h-10 shadow-[inset_0_-1px_0_0_color-mix(in_srgb,var(--app-border-strong),transparent)]",
				"[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
				className,
			)}
			style={
				isRunbook
					? {
							boxShadow:
								"inset 0 -1px 0 0 color-mix(in srgb, var(--app-text-muted) 20%, transparent)",
							...style,
						}
					: style
			}
			{...props}
		/>
	);
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
	return (
		<td
			data-slot="table-cell"
			className={cn(
				"p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
				className,
			)}
			{...props}
		/>
	);
}

function TableCaption({
	className,
	...props
}: React.ComponentProps<"caption">) {
	return (
		<caption
			data-slot="table-caption"
			className={cn("text-app-text-subtle mt-4 text-sm", className)}
			{...props}
		/>
	);
}

export {
	Table,
	TableHeader,
	TableBody,
	TableFooter,
	TableHead,
	TableRow,
	TableCell,
	TableCaption,
};
